import { useCallback, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { CheckCircle2, CircleX, PartyPopper, RotateCcw } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { cn } from "@/lib/utils"

export type QuizQuestion = {
  id: string
  question: string
  options: { id: string; label: string }[]
  correctId: string
  explanation?: string
}

type TrackQuizProps = {
  /** Full pool of questions — 4 will be randomly selected */
  questions: QuizQuestion[]
  onComplete: () => void
  trackName: string
  /** How many questions to show per attempt */
  count?: number
}

type AnswerState = {
  selectedId: string | null
  submitted: boolean
}

function shuffleAndPick<T>(items: T[], count: number): T[] {
  const shuffled = [...items].sort(() => Math.random() - 0.5)
  return shuffled.slice(0, count)
}

export function TrackQuiz({ questions, onComplete, trackName, count = 4 }: TrackQuizProps) {
  const { t } = useTranslation("", { keyPrefix: "training.quiz" })
  const [answers, setAnswers] = useState<Record<string, AnswerState | undefined>>({})
  const [round, setRound] = useState(0)

  // Pick random questions — re-shuffles on round change
  const activeQuestions = useMemo(
    () => shuffleAndPick(questions, Math.min(count, questions.length)),
    [questions, count, round]
  )

  const allAnswered = activeQuestions.every((q) => answers[q.id]?.submitted)
  const correctCount = activeQuestions.filter(
    (q) => answers[q.id]?.selectedId === q.correctId
  ).length

  function handleSelect(questionId: string, optionId: string) {
    if (answers[questionId]?.submitted) return
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { selectedId: optionId, submitted: false },
    }))
  }

  function handleSubmit(questionId: string) {
    setAnswers((prev) => {
      const existing = prev[questionId]
      if (!existing) return prev
      return { ...prev, [questionId]: { ...existing, submitted: true } }
    })
  }

  const handleRedo = useCallback(() => {
    setAnswers({})
    setRound((r) => r + 1)
  }, [])

  return (
    <div className="flex flex-col items-center gap-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold">{t("title", "Quick Quiz")}</h2>
        <p className="text-muted-foreground mt-2 text-base">
          {t("description", "Test your knowledge -- don't worry, this is just for learning!")}
        </p>
      </div>

      {/* Questions */}
      <div className="flex w-full max-w-2xl flex-col gap-6">
        {activeQuestions.map((q, idx) => {
          const answer = answers[q.id]
          const isSubmitted = answer?.submitted ?? false
          const isCorrect = isSubmitted && answer?.selectedId === q.correctId

          return (
            <Card key={q.id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
                  {t("question_label", "Question")} {idx + 1}/{activeQuestions.length}
                </p>
                <p className="text-lg font-semibold">{q.question}</p>
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                {q.options.map((opt) => {
                  const isSelected = answer?.selectedId === opt.id
                  const isOptionCorrect = opt.id === q.correctId

                  return (
                    <button
                      key={opt.id}
                      disabled={isSubmitted}
                      type="button"
                      className={cn(
                        "focus-visible:ring-ring flex items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none",
                        !isSubmitted && isSelected && "border-primary bg-primary/5",
                        !isSubmitted && !isSelected && "hover:bg-muted/50",
                        isSubmitted && isOptionCorrect && "border-success bg-success/15",
                        isSubmitted &&
                          isSelected &&
                          !isOptionCorrect &&
                          "border-destructive bg-destructive/15",
                        isSubmitted && "cursor-default"
                      )}
                      onClick={() => handleSelect(q.id, opt.id)}
                    >
                      <div
                        className={cn(
                          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold",
                          !isSubmitted &&
                            isSelected &&
                            "border-primary bg-primary text-primary-foreground",
                          !isSubmitted && !isSelected && "border-muted-foreground/30",
                          isSubmitted &&
                            isOptionCorrect &&
                            "border-foreground bg-success text-success-foreground",
                          isSubmitted &&
                            isSelected &&
                            !isOptionCorrect &&
                            "border-foreground bg-destructive text-destructive-foreground"
                        )}
                      >
                        {(() => {
                          if (isSubmitted && isOptionCorrect)
                            return <CheckCircle2 className="h-4 w-4" />
                          if (isSubmitted && isSelected && !isOptionCorrect)
                            return <CircleX className="h-4 w-4" />
                          return String.fromCharCode(65 + q.options.indexOf(opt))
                        })()}
                      </div>
                      <span>{opt.label}</span>
                    </button>
                  )
                })}

                {/* Feedback after submit */}
                {isSubmitted && (
                  <div
                    className={cn(
                      "mt-2 rounded-lg px-4 py-3 text-sm",
                      isCorrect ? "bg-success/15 text-foreground" : "bg-warning/30 text-warning-ink"
                    )}
                  >
                    {isCorrect
                      ? t("encouraging_correct", "Correct! Great job!")
                      : t(
                          "encouraging_incorrect",
                          "Not quite, but that's OK! The correct answer is highlighted above."
                        )}
                    {q.explanation && !isCorrect && (
                      <p className="mt-1 opacity-80">{q.explanation}</p>
                    )}
                  </div>
                )}

                {/* Submit button per question */}
                {!isSubmitted && answer?.selectedId && (
                  <Button className="mt-2 self-start" size="sm" onClick={() => handleSubmit(q.id)}>
                    {t("check_answer", "Check Answer")}
                  </Button>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Actions after completing all questions */}
      {allAnswered && (
        <div className="flex flex-col items-center gap-4 pb-8">
          <div className="text-center">
            <p className="text-lg font-semibold">
              {t("result", {
                correct: correctCount,
                total: activeQuestions.length,
                defaultValue: `You got {{correct}} out of {{total}} correct!`,
              })}
            </p>
            <p className="text-muted-foreground text-sm">
              {t("well_done", "Great effort! Every question is a learning opportunity.")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button className="gap-2" size="lg" onClick={() => void onComplete()}>
              <PartyPopper className="h-5 w-5" />
              {t("complete_track", { trackName, defaultValue: `Complete {{trackName}}` })}
            </Button>
            <Button className="gap-2" size="lg" variant="outline" onClick={handleRedo}>
              <RotateCcw className="h-4 w-4" />
              {t("redo", "Redo quiz")}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
